import _ from 'lodash';
import { createContext } from 'react';

import { bindServices } from '@/services/api';
import { toPaginate } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';

const api = bindServices('reports');
import { generateReportToken, updateFavorite } from './service';
import { filtersConfig } from './utils';
import { groupsFiltersConfig } from './manage-groups/utils';

import './utils';

export const ReportContext = createContext({});

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const initialState = {
  data: [],
  favorites: [],
  groups: [],
  searchCriteria: null,
  currentReport: null,
  currentGroup: null,
  newReport: false,
  newGroup: false,
  pagination: meta,
  groupsPagination: meta,
  reportToken: null,
  filtersConfig,
  groupsFiltersConfig,
  favoritesChanged: false,
};

const Model = {
  namespace: 'reports',
  state: initialState,
  effects: {
    *fetchGroups({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'report/groups',
          ...CoreQueries.ManageReports.groups,
          ...payload,
        });
        yield put({
          type: 'queryGroups',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *fetchReports({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'report/allowed',
          ...CoreQueries.ManageReports.reports,
          ...payload,
        });
        yield put({
          type: 'queryReports',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *fetchFavorites({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'report/favorites',
          ...CoreQueries.ManageReports.favorites,
          ...payload,
        });
        yield put({ type: 'queryFavorites', payload: res });
      } catch (e) {}
    },

    *fetchReportById({ payload }, { call, put }) {
      try {
        const res = yield call(api.getById, {
          route: 'report',
          id: payload,
          ...CoreQueries.ManageReports.report,
        });
        yield put({ type: 'queryReportById', payload: res });
      } catch (e) {}
    },

    *powerBIEmbed({ payload }, { call, put }) {
      try {
        const res = yield call(generateReportToken, payload);
        yield put({ type: 'queryPowerBIEmbed', payload: res });
      } catch (e) {}
    },

    *setFavorite({ payload }, { call, put }) {
      try {
        yield call(updateFavorite, payload);
        yield put({ type: 'querySetFavorite', payload });

        yield put({ type: 'markFavoritesChanged' });
      } catch (e) {}
    },

    *toggleActive({ payload }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `report/activate/${payload.id}/${payload.isActive}`,
          method: 'post',
        });
        yield put({
          type: 'queryToggleActive',
          payload: { id: payload.id, isActive: payload.isActive },
        });
      } catch (e) {}
    },

    *addNewReport({ payload }, { put }) {
      yield put({ type: 'newReport', payload });
    },

    *addNewGroup({ payload }, { put }) {
      yield put({ type: 'newGroup', payload });
    },

    *search({ payload }, { put }) {
      yield put({ type: 'querySearch', payload });
    },

    *onTabChange({ payload }, { put, select }) {
      if (payload === 'favorites') {
        const { favoritesChanged } = yield select((state) => state.reports);
        if (favoritesChanged) {
          yield put({ type: 'fetchFavorites', payload: { page: 1 } });
          yield put({ type: 'clearFavoritesChanged' });
        }
      }
    },

    *cleanReportData(o, { put }) {
      yield put({ type: 'cleanupReportData' });
    },

    *deleteItem({ id, params }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `report/${id}`,
          method: 'delete',
        });
        yield put({
          type: 'queryDeleteItem',
          payload: { id },
        });
        yield put({
          type: 'fetchReports',
          payload: { ...params, readPriority: true },
        });
      } catch (e) {}
    },

    *resetState(o, { put }) {
      yield put({ type: 'reset' });
    },
  },
  reducers: {
    queryGroups(state, { payload }) {
      return {
        ...state,
        groups: payload?.items || [],
        groupsPagination: {
          ...state.groupsPagination,
          ...toPaginate(payload),
        },
      };
    },

    queryReports(state, { payload }) {
      const data = payload?.items || [];
      return {
        ...state,
        data,
        currentGroup: _.head(data)?.reportGroup,
        pagination: {
          ...state.pagination,
          ...toPaginate(payload),
        },
      };
    },

    queryFavorites(state, { payload }) {
      return {
        ...state,
        favorites: payload,
      };
    },

    querySetFavorite(state, { payload }) {
      return {
        ...state,
        data: state.data.map((item) =>
          item.id === payload.id ? { ...item, isFavorite: !payload.isFavorite } : item,
        ),
        favorites: payload.isFavorite
          ? state.favorites.filter((item) => item.id !== payload.id)
          : state.favorites,
      };
    },

    queryToggleActive(state, { payload }) {
      return {
        ...state,
        data: state.data.map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
        favorites: state.favorites.map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
      };
    },

    markFavoritesChanged(state) {
      return {
        ...state,
        favoritesChanged: true,
      };
    },

    clearFavoritesChanged(state) {
      return {
        ...state,
        favoritesChanged: false,
      };
    },

    queryReportById(state, { payload }) {
      return {
        ...state,
        currentReport: payload,
      };
    },

    queryPowerBIEmbed(state, { payload }) {
      return {
        ...state,
        reportToken: payload,
      };
    },

    queryManagement(state, { payload }) {
      return {
        ...state,
        newGroup: false,
        newReport: false,
        response: payload,
      };
    },

    addNewReport(state, { payload }) {
      return {
        ...state,
        newGroup: false,
        newReport: payload,
      };
    },

    addNewGroup(state, { payload }) {
      return {
        ...state,
        newReport: false,
        newGroup: payload,
      };
    },

    queryDeleteItem(state, { payload }) {
      return {
        ...state,
        data: state.data.filter((item) => item.id !== payload.id),
      };
    },

    querySearch(state, { payload }) {
      return {
        ...state,
        ...(_.isString(payload) && { searchCriteria: payload }),
        ...(_.isObject(payload) && {
          [`search${_.capitalize(payload.viewContext)}Criteria`]: payload.criteria,
        }),
      };
    },

    cleanupReportData(state) {
      return {
        ...state,
        reportToken: null,
        currentReport: null,
      };
    },

    cleanUp(state) {
      return {
        ...state,
        newGroup: false,
        newReport: false,
        currentGroup: null,
        currentReport: null,
        reportToken: null,
      };
    },

    reset() {
      return initialState;
    },
  },
};

export default Model;
